import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FileSpreadsheet, FileText, File } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { MODELOS_OPCOES, TIPOS_EXPORTACAO } from '@/lib/utils/helpers';
import { useExportacaoDocumentosTurbo } from '@/features/documentos/components/documentos-helpers';
import { useToast } from '@/hooks/use-toast';

interface ExportarModalSNProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipoDocumento: '0' | '1';
  cnpjContribuinte: string;
  dataInicio: string;
  dataFim: string;
}

export function ExportarModalSN({
  open,
  onOpenChange,
  tipoDocumento,
  cnpjContribuinte,
  dataInicio,
  dataFim,
}: ExportarModalSNProps) {
  const [tipoArquivo, setTipoArquivo] = useState<'excel' | 'pdf' | 'csv' | ''>('');
  const [modeloSelecionado, setModeloSelecionado] = useState<string>('');
  const [entradaSaida, setEntradaSaida] = useState<'0' | '1'>(tipoDocumento);
  const [dataEmissaoDe, setDataEmissaoDe] = useState<string>(dataInicio);
  const [dataEmissaoAte, setDataEmissaoAte] = useState<string>(dataFim);
  const { toast } = useToast();

  const exportacao = useExportacaoDocumentosTurbo({
    dataEmissaoDe,
    dataEmissaoAte,
    modeloDocumento: modeloSelecionado,
    entradaSaida,
    tipoArquivo,
    cnpjContribuinte: cnpjContribuinte || undefined,
  });

  const resetForm = useCallback(() => {
    setTipoArquivo('');
    setModeloSelecionado('');
    setEntradaSaida(tipoDocumento);
    setDataEmissaoDe(dataInicio);
    setDataEmissaoAte(dataFim);
  }, [tipoDocumento, dataInicio, dataFim]);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const handleExportar = async () => {
    if (exportacao.isPending) return;

    toast({
      title: 'Processando exportação',
      description: 'O documento está sendo processado e preparado para download.',
    });

    const ok = await exportacao.exportar();
    if (!ok) return;
    onOpenChange(false);
    resetForm();
  };

  const handleFechar = () => {
    onOpenChange(false);
    resetForm();
  };

  const { isFormValid, isPeriodoValido } = exportacao;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-8">
        <DialogHeader>
          <DialogTitle>Exportar Documentos</DialogTitle>
          <DialogDescription>
            Selecione o modelo de documento e o formato de exportação
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="flex flex-col gap-2">
            <Label className="text-base font-medium">
              Modelo do Documento <span className="text-red-500">*</span>
            </Label>
            <Select value={modeloSelecionado} onValueChange={setModeloSelecionado}>
              <SelectTrigger className="w-full h-11">
                <SelectValue placeholder="Selecione o modelo" />
              </SelectTrigger>
              <SelectContent>
                {MODELOS_OPCOES.map((modelo: { value: string; label: string }) => (
                  <SelectItem key={modelo.value} value={modelo.value}>
                    {modelo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">
              Entrada / Saída <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant={entradaSaida === '0' ? 'default' : 'outline'}
                onClick={() => setEntradaSaida('0')}
              >
                Entrada
              </Button>
              <Button
                type="button"
                variant={entradaSaida === '1' ? 'default' : 'outline'}
                onClick={() => setEntradaSaida('1')}
              >
                Saída
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">
              Período de Emissão <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label className="text-sm text-muted-foreground">De</Label>
                <Input
                  type="date"
                  value={dataEmissaoDe}
                  onChange={e => setDataEmissaoDe(e.target.value)}
                  aria-invalid={!!dataEmissaoDe && !!dataEmissaoAte && !isPeriodoValido}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm text-muted-foreground">Até</Label>
                <Input
                  type="date"
                  value={dataEmissaoAte}
                  onChange={e => setDataEmissaoAte(e.target.value)}
                  aria-invalid={!!dataEmissaoDe && !!dataEmissaoAte && !isPeriodoValido}
                />
              </div>
            </div>
            {!isPeriodoValido && (
              <p className="text-xs text-destructive">O período informado é inválido.</p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">
              Formato de Exportação <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {TIPOS_EXPORTACAO.map(
                (tipo: { value: string; label: string; extension: string; icon: string }) => {
                  const Icon =
                    tipo.icon === 'FileSpreadsheet'
                      ? FileSpreadsheet
                      : tipo.icon === 'FileText'
                        ? FileText
                        : File;
                  const isSelected = tipoArquivo === tipo.value;

                  return (
                    <button
                      key={tipo.value}
                      type="button"
                      onClick={() => setTipoArquivo(tipo.value as 'excel' | 'pdf' | 'csv')}
                      className={`flex flex-col items-center justify-center gap-3 p-6 rounded-lg border-2 transition-all hover:bg-accent/50 ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border bg-background'
                      }`}
                    >
                      <Icon
                        className={`h-8 w-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <div className="text-center">
                        <p
                          className={`font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}
                        >
                          {tipo.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{tipo.extension}</p>
                      </div>
                    </button>
                  );
                }
              )}
            </div>
          </div>

          {(!isFormValid || !isPeriodoValido) && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <span className="font-medium">Atenção:</span> Selecione o modelo do documento e o
                formato de exportação, o tipo (Entrada/Saída) e o período para continuar.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={handleFechar}>
            Cancelar
          </Button>
          <Button
            onClick={handleExportar}
            disabled={!isFormValid || !isPeriodoValido || exportacao.isPending}
          >
            {exportacao.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner />
                Exportando...
              </span>
            ) : (
              'Exportar'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
